import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBlocker } from 'react-router';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';
import { message, Alert, Progress, Spin, Popover, Switch } from 'antd';
import { IoChevronDown, IoSend, IoClose, IoMic, IoMicOff, IoStop } from 'react-icons/io5';
import ReactMarkdown from 'react-markdown';
import EmptyR from "@/pages/AfterMeet/static/ReadyGen.png";
import FormPopover from './FormPopover';
import InMeetResultCard from './InMeetResultCard';
import { type TemplateMode } from './templateOptions';
import { useRealtimeTranscription } from '@/hooks/useRealtimeTranscription';
import { useSupabaseAuth } from '@/utils/store/supabaseAuth';
import { TranscriptionStore, addTranscriptionItem, resetTranscriptions } from '@/utils/store/transcription';
import { MeetingDurationStore, resetMeetingTimer } from '@/utils/store/meetingDuration';
import { FeatureFlagsStore, setOfflineTranscriptionFirst } from '@/utils/store/featureFlags';
import { SubmitParams, checkSubmitParams, validateSubmitParams } from '@/@types/submitParams';
import { ApiService } from '@/services/api';
import { saveHistoryAndEvaluation } from '@/services/historyService';
import { uploadAndTranscribeAudio } from '@/utils/file-upload';

// 对话记录类型
interface ChatMessage {
  id: string;
  type: 'note' | 'chat';  // note=记笔记, chat=问问题
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

/**
 * 实时会议页面组件
 * 左右两栏布局，比例2:3
 */
const InMeet: React.FC = () => {
  const { isAuthenticated, id, user, isLoading } = useSupabaseAuth();
  
  // 使用功能开关 store
  const featureFlagsSnap = useSnapshot(FeatureFlagsStore);
  
  // 会议状态：pending=待发起，active=进行中
  const [meetingStatus, setMeetingStatus] = useState<'pending' | 'active'>('pending');
  
  // 使用 Valtio store 管理转录记录
  const transcriptionSnap = useSnapshot(TranscriptionStore);
  
  // 结束会议时的loading状态
  const [isEndingMeeting, setIsEndingMeeting] = useState(false);
  
  // 离线转写进度状态
  const [offlineTranscriptionStatus, setOfflineTranscriptionStatus] = useState<string>('');
  
  // 会议结果
  const [meetingResult, setMeetingResult] = useState<any>(null);
  
  // 对话输入
  const [chatInput, setChatInput] = useState('');
  
  // 对话记录
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    // { id: '1', role: 'assistant', content: '您好，请问有什么可以帮助您的？', timestamp: new Date() },
  ]);
  
  // 底部选项卡片 - 选中状态
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  
  // 是否显示行业/模板浮层（整理纪要确认后显示）
  const [showFormPopover, setShowFormPopover] = useState(false);
  
  // 对话模式：记笔记 或 问问题
  const [chatMode, setChatMode] = useState<'note' | 'question'>('note');
  
  // 模式选择下拉菜单状态
  const [modeDropdownOpen, setModeDropdownOpen] = useState(false);
  
  // 格式化时长为 mm:ss
  const formatDuration = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);
  
  // 使用实时转录 hook
  const {
    isRecording,
    finalText,
    partialText,
    error: transcriptionError,
    microphonePermission,
    permissionError,
    durationState,
    startRecording,
    stopRecording,
    clearTranscription,
    requestMicrophonePermission,
    getRecordingFile, // 获取录音文件
  } = useRealtimeTranscription({
    moduleName: 'InMeet',
    onStart: () => {
      console.log('InMeet: 录音已开始');
    },
    onStop: (transcription) => {
      console.log('InMeet: 录音已停止，转录文本长度:', transcription.length);
    },
    onTranscriptionUpdate: (newText, fullText) => {
      // 每次有新的 final 文本时，通过 store 添加并自动触发合并
      // 直接从 Valtio proxy 读取本次会议时长，无闭包过期问题
      if (newText.trim()) {
        addTranscriptionItem('发言人', String(MeetingDurationStore.currentDuration), newText.trim());
      }
    },
    onError: (err) => {
      console.error('InMeet: 转录错误:', err);
    },
  });
  
  // 会议开始时间
  const [meetingStartTime] = useState(new Date());

  // 本次会议录音时长（Valtio proxy，支持暂停/恢复，回调中无闭包问题）
  const meetingDurationSnap = useSnapshot(MeetingDurationStore);

  // 模板模式
  const [templateMode, setTemplateMode] = useState<TemplateMode>('classic');
  
  // 页面滚动容器 ref
  const pageScrollRef = useRef<HTMLDivElement>(null);
  
  // 切换模板模式时清空选中的模板
  useEffect(() => {
    setSelectedTemplate('');
  }, [templateMode]);
  
  // 结果出现时自动滚动到结果卡片
  useEffect(() => {
    if ((isEndingMeeting || meetingResult) && pageScrollRef.current) {
      // 延迟一帧等待 DOM 渲染
      requestAnimationFrame(() => {
        pageScrollRef.current?.scrollTo({
          top: pageScrollRef.current.scrollHeight,
          behavior: 'smooth',
        });
      });
    }
  }, [isEndingMeeting, meetingResult]);
  
  // 发送消息
  const handleSendMessage = () => {
    if (!chatInput.trim()) return;
    
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      type: chatMode === 'note' ? 'note' : 'chat',
      role: 'user',
      content: chatInput,
      timestamp: new Date(),
    };
    
    setChatMessages([...chatMessages, newMessage]);
    setChatInput('');
  };
  
  // 删除消息
  const handleDeleteMessage = (id: string) => {
    setChatMessages(chatMessages.filter(msg => msg.id !== id));
  };
  
  // 开始会议
  const handleStartMeeting = () => {
    setMeetingStatus('active');
  };
  
  // 发起新会议（重置状态）
  const handleNewMeeting = () => {
    if (isRecording) {
      stopRecording();
    }
    setMeetingStatus('pending');
    resetTranscriptions();
    setChatMessages([]);
    setIsEndingMeeting(false);
    setMeetingResult(null);
    setShowFormPopover(false);
    clearTranscription();
    // 重置本次会议录音时长（不影响全局用量计数）
    resetMeetingTimer();
  };
  
  // 切换录音状态
  const handleToggleRecording = async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  };
  
  // 结束会议
  const handleEndMeeting = async () => {
    // 构建提交参数
    const notesContent = chatMessages
      .filter(msg => msg.type === 'note')
      .map(msg => msg.content)
      .join('\n');
    
    // 使用统一的参数验证
    const validation = validateSubmitParams({
      Content: notesContent || '无笔记',
      Industry: selectedIndustry,
      Output_type: selectedTemplate,
    });
    
    if (!validation.isValid) {
      const missingFieldMessages: Record<string, string> = {
        'Industry': '没有选择行业',
        'Output_type': '没有选择模板',
      };
      
      const firstMissing = validation.missingFields[0];
      message.warning(missingFieldMessages[firstMissing] || '必填项为空，请补充');
      return;
    }
    
    // 停止录音
    if (isRecording) {
      await stopRecording();
    }
    
    // 设置loading状态
    setIsEndingMeeting(true);
    
    try {
      let transcriptionText = '';
      
      // 根据功能开关决定是否优先使用离线转写
      if (featureFlagsSnap.useOfflineTranscriptionFirst) {
        // 尝试获取录音文件并进行离线转写
        const recordingFile = getRecordingFile();
        if (recordingFile) {
          console.log('InMeet: 开始离线转写音频文件');
          setOfflineTranscriptionStatus('正在上传音频文件...');
          
          try {
            // 使用统一的上传转录方法（包含预处理、上传、转录）
            // 录音文件已经是 16kHz 单声道 WAV，跳过音频预处理避免 DECODE_ERROR
            const transcriptionResult = await uploadAndTranscribeAudio(recordingFile, {
              onProgress: (progress) => {
                console.log(`上传进度: ${progress.percent}%`);
              },
              onStatusChange: (status) => {
                setOfflineTranscriptionStatus(status);
              },
              preprocessConfig: { enableMonoConversion: false },
            });
            
            transcriptionText = transcriptionResult.transcription;
            console.log('InMeet: 离线转写完成，文本长度:', transcriptionText.length);
            console.log('InMeet: [纪要来源=离线转录] 离线转录文本内容:\n', transcriptionText);
            setOfflineTranscriptionStatus('转录完成，正在生成纪要...');
            
          } catch (uploadError) {
            console.error('InMeet: 离线转写失败，回退到实时转录结果', uploadError);
            // 离线转写失败，使用实时转录结果作为备选
            transcriptionText = TranscriptionStore.items.map(t => t.content).join('\n') || finalText.trim();
            console.log('InMeet: [纪要来源=实时转录(离线失败)] 实时转录文本内容:\n', transcriptionText);
            setOfflineTranscriptionStatus('使用实时转录结果生成纪要...');
          }
        } else {
          // 没有录音文件，使用实时转录结果
          console.log('InMeet: 没有录音文件，使用实时转录结果');
          transcriptionText = TranscriptionStore.items.map(t => t.content).join('\n') || finalText.trim();
          console.log('InMeet: [纪要来源=实时转录(无录音文件)] 实时转录文本内容:\n', transcriptionText);
          setOfflineTranscriptionStatus('使用实时转录结果生成纪要...');
        }
      } else {
        // 功能开关关闭，直接使用实时转录结果
        console.log('InMeet: 功能开关已关闭，直接使用实时转录结果');
        transcriptionText = TranscriptionStore.items.map(t => t.content).join('\n') || finalText.trim();
        console.log('InMeet: [纪要来源=实时转录(开关关闭)] 实时转录文本内容:\n', transcriptionText);
        setOfflineTranscriptionStatus('使用实时转录结果生成纪要...');
      }
      
      // 检查是否有转录内容
      if (!transcriptionText.trim() && !notesContent.trim()) {
        message.warning('没有会议内容，不能结束会议');
        setIsEndingMeeting(false);
        setOfflineTranscriptionStatus('');
        return;
      }
      
      // 构建最终内容
      const finalContent = `${notesContent}\n\n=== 会议转录 ===\n${transcriptionText}`;
      
      // 调用 chatRun API 生成纪要
      const response = await ApiService.chatRun({
        app_id: '',
        workflow_id: '',
        parameters: {
          Content: finalContent,
          Industry: selectedIndustry,
          Output_type: selectedTemplate,
          app_id: '1',
          audioNum: transcriptionText ? 1 : 0,
          textNum: 0,
          files: '',
        }
      });
      
      // 保存结果
      setMeetingResult(response.data);
      setOfflineTranscriptionStatus('');
      
      // 如果登录了，保存历史记录
      if (isAuthenticated && !isLoading && id && response.data?.data) {
        const resultData = JSON.parse(response.data.data)?.data || '';
        await saveHistoryAndEvaluation({
          userId: id,
          email: user?.email,
          result: resultData,
          input: {
            Content: finalContent,
            Industry: selectedIndustry,
            Output_type: selectedTemplate,
          },
        });
      }
      
      message.success('会议整理完成');
    } catch (error) {
      console.error('会议整理失败:', error);
      message.error('会议整理失败，请重试');
      setIsEndingMeeting(false);
      setOfflineTranscriptionStatus('');
    }
  };
  
  // 路由拦截 - 会议进行中或录音中离开时提示
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      (meetingStatus === 'active' || isRecording) && !meetingResult && currentLocation.pathname !== nextLocation.pathname
  );
  
  // 发起会议时申请麦克风权限
  useEffect(() => {
    if (meetingStatus === 'active' && microphonePermission === 'pending') {
      requestMicrophonePermission();
    }
  }, [meetingStatus, microphonePermission, requestMicrophonePermission]);

  // 浏览器关闭/刷新拦截
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if ((meetingStatus === 'active' || isRecording) && !meetingResult) {
        e.preventDefault();
        e.returnValue = '会议正在进行中，离开将中断当前结果';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [meetingStatus, isRecording]);
  
  return (
    <div className="text-black h-full overflow-y-auto" ref={pageScrollRef}>
      {/* 离开确认弹窗 */}
      {blocker.state === 'blocked' && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl border border-[#E9E9E7]">
            <div className="text-lg font-semibold text-[#37352F] mb-2">是否要离开？</div>
            <div className="text-sm text-[#91918E] mb-6">会议正在进行中，离开将中断当前结果</div>
            <div className="flex gap-3 justify-end">
              <button
                className="px-4 py-2 text-sm text-[#73726E] hover:bg-[#F7F7F5] rounded-xl transition-colors cursor-pointer"
                onClick={() => blocker.reset?.()}
              >
                取消
              </button>
              <button
                className="px-4 py-2 text-sm bg-red-500 text-white hover:bg-red-600 rounded-xl transition-colors shadow-sm cursor-pointer"
                onClick={() => blocker.proceed?.()}
              >
                确定中断
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex flex-row min-h-full">
        {/* 左侧 - 纪要助手（无卡片轮廓） */}
        <div className="flex-1 min-w-[380px] flex flex-col overflow-hidden">
          <div className="flex flex-col h-full px-8 py-6">
            {/* 纪要助手标题 */}
            <div className="flex items-center gap-2.5 mb-5 flex-shrink-0">
              <div className="w-8 h-8 rounded-lg bg-[#F7F7F5] border border-[#E9E9E7] flex items-center justify-center">
                <svg className="w-4 h-4 text-[#37352F]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              </div>
              <span className="text-base font-semibold text-[#37352F]">纪要助手</span>
            </div>

            {/* 输入区域 */}
            <div className="flex-shrink-0 mb-4">
              <div className="border border-[#E9E9E7] rounded-xl bg-white">
                <textarea
                  className="w-full min-h-[140px] p-4 bg-transparent border-none resize-none text-sm text-[#37352F] placeholder:text-[#91918E] focus:outline-none leading-relaxed"
                  placeholder={chatMode === 'note' ? '输入会议笔记...' : '输入您的问题...'}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                {/* 底部工具栏 */}
                <div className="flex items-center justify-between px-4 pb-3 pt-2 border-t border-[#E9E9E7]">
                  {/* 模式选择器 */}
                  <div className="relative">
                    <button
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#73726E] bg-[#F7F7F5] border border-[#E9E9E7] rounded-lg hover:bg-[#EDEDEC] transition-colors cursor-pointer"
                      onClick={() => setModeDropdownOpen(!modeDropdownOpen)}
                      onBlur={() => setTimeout(() => setModeDropdownOpen(false), 150)}
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        {chatMode === 'note' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                        )}
                      </svg>
                      <span>{chatMode === 'note' ? '记笔记' : '问问题'}</span>
                      <IoChevronDown className={`w-3 h-3 transition-transform duration-200 ${modeDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {/* 下拉菜单 */}
                    {modeDropdownOpen && (
                      <div className="absolute left-0 bottom-full mb-1.5 bg-white border border-[#E9E9E7] rounded-xl shadow-lg overflow-hidden z-10 min-w-max">
                        <button
                          className={`w-full px-4 py-2.5 text-xs text-left hover:bg-[#F7F7F5] transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                            chatMode === 'note' ? 'text-[#37352F] bg-[#F7F7F5] font-medium' : 'text-[#73726E]'
                          }`}
                          onClick={() => { setChatMode('note'); setModeDropdownOpen(false); }}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                          </svg>
                          <span>记笔记</span>
                        </button>
                        <button
                          className="w-full px-4 py-2.5 text-xs text-left flex items-center gap-2 text-[#C3C2BF] cursor-not-allowed whitespace-nowrap"
                          disabled
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                          </svg>
                          <span>问问题</span>
                          <span className="text-[10px] bg-[#F7F7F5] text-[#91918E] px-1.5 py-0.5 rounded-md">开发中</span>
                        </button>
                      </div>
                    )}
                  </div>
                  {/* 发送按钮 */}
                  <button 
                    className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                      chatInput.trim() 
                        ? 'bg-black text-white hover:bg-zinc-800' 
                        : 'bg-[#F7F7F5] text-[#C3C2BF] cursor-not-allowed'
                    }`}
                    onClick={handleSendMessage}
                    disabled={!chatInput.trim()}
                  >
                    <span>发送</span>
                    <IoSend className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* 对话记录 */}
            <div className="flex-1 overflow-y-auto mb-4 space-y-2.5">
              {chatMessages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-[#91918E] text-sm">
                  暂无记录
                </div>
              ) : (
                chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`group relative rounded-xl text-sm transition-colors ${
                      msg.type === 'note'
                        ? 'bg-[#FBF9F3] border border-[#E9E5D5]'
                        : msg.role === 'assistant'
                          ? 'bg-[#F7F7F5] border border-[#E9E9E7] mr-6'
                          : 'bg-[#F0F4FF] border border-[#D8E0F0] ml-6'
                    }`}
                  >
                    {/* 消息头部 */}
                    <div className="flex items-center justify-between px-3.5 pt-2.5">
                      {msg.type === 'note' ? (
                        <span className="text-xs text-[#9B8B6E] font-medium flex items-center gap-1.5">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                          </svg>
                          笔记
                        </span>
                      ) : msg.role === 'assistant' ? (
                        <span className="text-xs text-[#73726E] font-medium flex items-center gap-1.5">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                          </svg>
                          助手
                        </span>
                      ) : (
                        <span className="text-xs text-[#5B7BB5] font-medium">我</span>
                      )}
                      <div className="flex items-center gap-2">
                        {msg.type === 'note' && (
                          <span className="text-xs text-[#91918E]">
                            {dayjs(msg.timestamp).format('HH:mm:ss')}
                          </span>
                        )}
                        {/* 仅note类型显示删除按钮 */}
                        {msg.type === 'note' && (
                          <button
                            className="w-5 h-5 text-[#C3C2BF] hover:text-red-400 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 cursor-pointer"
                            onClick={() => handleDeleteMessage(msg.id)}
                          >
                            <IoClose className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* 消息内容 */}
                    <div className="px-3.5 pb-3 pt-1.5">
                      {msg.type === 'chat' && msg.role === 'user' ? (
                        <div className="prose prose-sm max-w-none text-[#37352F]">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <div className="text-[#37352F] leading-relaxed whitespace-pre-wrap text-sm">
                          {msg.content}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
          </div>
        </div>
        
        {/* 中间虚线分隔 */}
        <div className="flex-shrink-0 w-px border-l border-dashed border-[#D5D5D3] my-6" />
        
        {/* 右侧 - 会议录音与转录（无卡片轮廓） */}
        <div className="flex-1 min-w-[480px] flex flex-col overflow-hidden relative">
          {/* 右上角按钮区域 */}
          <div className="absolute top-5 right-8 flex items-center gap-2 z-10">
            {/* 转写模式设置弹出菜单 */}
            <Popover
              content={
                <div className="w-72">
                  <div className="flex items-center justify-between py-2">
                    <div className="flex flex-col gap-1">
                      <div className="text-sm font-medium text-[#37352F]">离线高精度转写</div>
                      <div className="text-xs text-[#91918E]">
                        {featureFlagsSnap.useOfflineTranscriptionFirst 
                          ? '使用录音文件离线转写（更准确但较慢）' 
                          : '使用实时转写结果（更快但可能略微不准确）'}
                      </div>
                    </div>
                    <Switch
                      checked={featureFlagsSnap.useOfflineTranscriptionFirst}
                      onChange={(checked) => setOfflineTranscriptionFirst(checked)}
                      size="small"
                    />
                  </div>
                </div>
              }
              title="转写设置"
              trigger="click"
              placement="bottomRight"
            >
              <button className="px-3 py-1.5 text-sm text-[#91918E] hover:text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer">
                <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                设置
              </button>
            </Popover>
            
            {/* 发起新会议按钮 */}
            <button
              className="px-3 py-1.5 text-sm text-[#91918E] hover:text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer"
              onClick={handleNewMeeting}
            >
              + 发起新会议
            </button>
          </div>

          <div className="flex flex-col h-full px-8 py-6">
            {meetingStatus === 'pending' ? (
              /* 待发起会议状态 */
              <div className="flex flex-col items-center justify-center h-full text-[#91918E]">
                <img src={EmptyR} alt="待发起会议" className="w-42 mb-5 opacity-80" />
                <div className="text-xl font-semibold text-[#37352F] mb-5">待发起会议</div>
                {/* Tips 提示 */}
                <div className="bg-[#F7F7F5] rounded-xl p-5 mb-6 max-w-md border border-[#E9E9E7]">
                  <div className="text-sm text-[#37352F] mb-3 font-medium">好的会议结果依赖：</div>
                  <div className="space-y-2.5 text-xs text-[#73726E]">
                    <div className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#E9E9E7] text-[#37352F] flex items-center justify-center text-xs font-medium flex-shrink-0">1</span>
                      <span className="pt-0.5">必要的观点做实时笔记</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#F7F7F5] border border-[#E9E9E7] text-[#91918E] flex items-center justify-center text-xs font-medium flex-shrink-0">2</span>
                      <div className="flex-1 pt-0.5">
                        <span className="text-[#91918E]">会中和助手交流获取新的灵感并和伙伴们讨论</span>
                        <span className="ml-1.5 text-[10px] bg-[#E9E9E7] text-[#91918E] px-1.5 py-0.5 rounded-md">开发中</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#F7F7F5] border border-[#E9E9E7] text-[#91918E] flex items-center justify-center text-xs font-medium flex-shrink-0">3</span>
                      <div className="flex-1 pt-0.5">
                        <span className="text-[#91918E]">短暂离开回来后，让助手总结之前模块</span>
                        <span className="ml-1.5 text-[10px] bg-[#E9E9E7] text-[#91918E] px-1.5 py-0.5 rounded-md">开发中</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  className="px-8 py-3 bg-black text-white text-sm font-medium rounded-full hover:bg-zinc-800 transition-colors cursor-pointer"
                  onClick={handleStartMeeting}
                >
                  发起会议
                </button>
              </div>
            ) : (
              /* 会议进行中状态 */
              <>
                {/* 标题区域 */}
                <div className="flex-shrink-0 mb-5">
                  <div className="text-xl font-semibold text-[#37352F] tracking-tight">
                    {dayjs(meetingStartTime).format('YYYY年M月D日')} - 新会议
                  </div>
                  <div className="text-sm text-[#91918E] mt-1">
                    {dayjs(meetingStartTime).format('HH:mm:ss')}
                  </div>
                </div>
                
                {/* 麦克风权限提示 */}
                {microphonePermission === 'denied' && (
                  <Alert
                    message="麦克风权限被拒绝"
                    description={permissionError || '请在浏览器设置中允许麦克风访问权限'}
                    type="error"
                    showIcon
                    className="mb-4 rounded-xl"
                    action={
                      <button
                        className="text-xs text-blue-500 hover:text-blue-600 font-medium cursor-pointer"
                        onClick={requestMicrophonePermission}
                      >
                        重新申请
                      </button>
                    }
                  />
                )}
                
                {/* 转录错误提示 */}
                {transcriptionError && (
                  <Alert
                    message="转录错误"
                    description={transcriptionError}
                    type="error"
                    showIcon
                    closable
                    className="mb-4 rounded-xl"
                    action={
                      transcriptionError.includes('请重试') ? (
                        <button
                          className="text-xs text-blue-500 hover:text-blue-600 font-medium cursor-pointer"
                          onClick={async () => {
                            // 停止当前录音（如果有）
                            if (isRecording) {
                              await stopRecording();
                            }
                            // 重新开始录音
                            await startRecording();
                          }}
                        >
                          重新录音
                        </button>
                      ) : undefined
                    }
                  />
                )}
                
                {/* 录音状态区域 */}
                <div className="flex items-center justify-between p-4 bg-[#FBFBFA] rounded-xl mb-4 flex-shrink-0 border border-[#E9E9E7]">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          isRecording 
                            ? 'bg-red-500 animate-pulse' 
                            : 'bg-[#C3C2BF]'
                        }`}
                      />
                      <span className="text-sm text-[#37352F] font-medium">
                        {isRecording ? '录音中...' : '未开始录音'}
                      </span>
                      {/* 录音时长显示 */}
                      {(isRecording || meetingDurationSnap.currentDuration > 0) && (
                        <span className="text-sm text-[#91918E] ml-2 font-mono">
                          {formatDuration(meetingDurationSnap.currentDuration)}
                        </span>
                      )}
                    </div>
                    <button
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                        isRecording 
                          ? 'bg-red-500 text-white hover:bg-red-600' 
                          : 'bg-black text-white hover:bg-zinc-800'
                      } ${microphonePermission === 'denied' || !isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={handleToggleRecording}
                      disabled={microphonePermission === 'denied' || !isAuthenticated}
                    >
                      {isRecording ? (
                        <><IoStop className="w-4 h-4" /> 停止录音</>
                      ) : (
                        <><IoMic className="w-4 h-4" /> 开始录音</>
                      )}
                    </button>
                  </div>
                  <button
                    className={`px-4 py-2 text-sm border rounded-full transition-colors flex items-center gap-2 ${
                      isEndingMeeting || (transcriptionSnap.items.length === 0 && chatMessages.length === 0)
                        ? 'text-[#C3C2BF] border-[#E9E9E7] cursor-not-allowed'
                        : 'text-[#73726E] border-[#E9E9E7] hover:bg-[#F7F7F5] hover:border-[#D5D5D3] cursor-pointer'
                    }`}
                    onClick={() => setShowFormPopover(true)}
                    disabled={isEndingMeeting || (transcriptionSnap.items.length === 0 && chatMessages.length === 0)}
                  >
                    {meetingResult ? (
                      ''
                    ) : isEndingMeeting ? (
                      <>
                        <Spin size="small" />
                        处理中...
                      </>
                    ) : (
                      '整理纪要'
                    )}
                  </button>
                  {/* 重新录音按钮 - 当错误消息包含"请重试"时显示 */}
                  {transcriptionError && transcriptionError.includes('请重试') && (
                    <button
                      className="px-4 py-2 text-sm text-blue-600 border border-blue-200 rounded-full hover:bg-blue-50 transition-colors flex items-center gap-2 cursor-pointer"
                      onClick={async () => {
                        // 停止当前录音（如果有）
                        if (isRecording) {
                          await stopRecording();
                        }
                        // 重新开始录音
                        await startRecording();
                      }}
                    >
                      重新录音
                    </button>
                  )}
                </div>
                
                {/* 录音时长进度条 */}
                {durationState.maxDuration > 0 && (
                  <div className="mb-4 px-1">
                    <Progress 
                      percent={durationState.getUsagePercentage()} 
                      size="small"
                      status={durationState.isLimitReached ? 'exception' : 'active'}
                      showInfo={false}
                    />
                    <div className="flex justify-between text-xs text-[#91918E] mt-1.5">
                      <span>剩余: {Math.floor((durationState.maxDuration - durationState.currentDuration) / 60)} 分钟</span>
                      <span>上限: {Math.floor(durationState.maxDuration / 60)} 分钟</span>
                    </div>
                  </div>
                )}
                
                {/* 发言记录区域 */}
                <div className="flex-1 overflow-y-auto">
                  {transcriptionSnap.items.length === 0 && !partialText && !isEndingMeeting && !meetingResult ? (
                    <div className="flex items-center justify-center h-full text-[#91918E] text-sm">
                      {isRecording ? '正在等待转录结果...' : '开始录音后，转录内容将显示在这里'}
                    </div>
                  ) : (
                    <>
                      {transcriptionSnap.items.map((item) => (
                        <div key={item.id} className="p-4 bg-[#FBFBFA] rounded-xl mb-3 border border-[#E9E9E7]">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-[#37352F]">{item.speaker}</span>
                            <span className="text-xs text-[#91918E] font-mono">{formatDuration(Number(item.startTime))}</span>
                          </div>
                          <div className="text-sm text-[#73726E] leading-relaxed">{item.content}</div>
                        </div>
                      ))}
                      {/* 实时转录中的部分文本 */}
                      {partialText && (
                        <div className="p-4 bg-[#F0F4FF] rounded-xl mb-3 border border-[#D8E0F0]">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-[#5B7BB5]">转录中...</span>
                            <span className="text-xs text-[#8BA3D1] font-mono">{durationState.getFormattedDuration()}</span>
                          </div>
                          <div className="text-sm text-[#5B7BB5] leading-relaxed italic">{partialText}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* 全宽结果卡片 — 双栏布局下方，与 AfterMeet 风格一致 */}
      {(isEndingMeeting || meetingResult) && (
        <InMeetResultCard
          meetingResult={meetingResult}
          isEndingMeeting={isEndingMeeting}
          offlineTranscriptionStatus={offlineTranscriptionStatus}
        />
      )}
      
      {/* 右下角浮层 - 行业/模板选择 */}
      {showFormPopover && !isEndingMeeting && (
        <FormPopover
          selectedIndustry={selectedIndustry}
          onSelectIndustry={setSelectedIndustry}
          selectedTemplate={selectedTemplate}
          onSelectTemplate={setSelectedTemplate}
          templateMode={templateMode}
          onChangeTemplateMode={setTemplateMode}
          onClose={() => setShowFormPopover(false)}
          onSubmit={() => {
            setShowFormPopover(false);
            handleEndMeeting();
          }}
        />
      )}
    </div>
  );
};

export default InMeet;
